// Mirror of src/marine_detect/danger_zone.py — point-in-polygon over a normalized
// polygon, scaled to whatever frame size is in use.

import type { PolygonPoint } from "../inference/types";

export class DangerZone {
  readonly polygonNorm: PolygonPoint[];

  constructor(polygonNorm: PolygonPoint[]) {
    if (polygonNorm.length < 3) {
      throw new Error("Danger zone polygon needs at least 3 vertices.");
    }
    for (const { x, y } of polygonNorm) {
      if (x < 0 || x > 1 || y < 0 || y > 1) {
        throw new Error(`Danger zone vertex (${x}, ${y}) is outside the [0, 1] range.`);
      }
    }
    this.polygonNorm = polygonNorm;
  }

  /** Returns the polygon in pixel coordinates for `(width, height)`. */
  pixelPolygon(frameSize: { w: number; h: number }): { x: number; y: number }[] {
    return this.polygonNorm.map(({ x, y }) => ({
      x: Math.round(x * frameSize.w),
      y: Math.round(y * frameSize.h),
    }));
  }

  /** True if `point` (pixel coords) is inside or on the edge of the polygon. */
  contains(point: { x: number; y: number }, frameSize: { w: number; h: number }): boolean {
    const poly = this.pixelPolygon(frameSize);
    return pointInPolygon(point, poly);
  }
}

/** Standard ray-casting test. Returns true if `p` is inside (or on the edge of) `poly`. */
export function pointInPolygon(
  p: { x: number; y: number },
  poly: { x: number; y: number }[],
): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i]!;
    const b = poly[j]!;
    // Treat points on a vertex or edge as "inside" — matches OpenCV's
    // pointPolygonTest(measureDist=False) >= 0 semantics.
    if (onSegment(p, a, b)) return true;
    const intersects =
      a.y > p.y !== b.y > p.y &&
      p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x;
    if (intersects) inside = !inside;
  }
  return inside;
}

function onSegment(
  p: { x: number; y: number },
  a: { x: number; y: number },
  b: { x: number; y: number },
): boolean {
  const cross = (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);
  if (Math.abs(cross) > 1e-6) return false;
  const minX = Math.min(a.x, b.x);
  const maxX = Math.max(a.x, b.x);
  const minY = Math.min(a.y, b.y);
  const maxY = Math.max(a.y, b.y);
  return p.x >= minX && p.x <= maxX && p.y >= minY && p.y <= maxY;
}
